/**
 * Publicidad (1.0 AdBanner + platform_config/ad_server): inventario vendido a anunciantes
 * (home_top / home_feed / home_bottom / product_sidebar) y configuración del ad server (proveedor
 * activo, frecuencia en el feed, ids públicos de Google AdSense y Meta Audience Network — ningún
 * secreto). `adBanners/{id}` lo escribe solo el servidor (admin-saveAdBanner); el cliente lee los
 * activos y afina fechas y país en memoria, como la 1.0.
 */
import { z } from 'zod';
import { CountryCodeSchema, TimestampSchema, UserIdSchema } from './primitives.js';

export const AD_PLACEMENTS = ['home_top', 'home_feed', 'home_bottom', 'product_sidebar'] as const;
export type AdPlacement = (typeof AD_PLACEMENTS)[number];
export const AdPlacementSchema = z.enum(AD_PLACEMENTS);

export const AD_PROVIDERS = ['manual', 'google', 'meta', 'hybrid'] as const;
export type AdProvider = (typeof AD_PROVIDERS)[number];

/** Medidas recomendadas por placement (1.0 PLACEMENT_SPECS), para el admin. */
export const AD_PLACEMENT_SPECS: Record<
  AdPlacement,
  { ratio: string; size: string; hint: string }
> = {
  home_top: { ratio: '4:1', size: '1280 × 320 px', hint: 'Banner ancho encima del catálogo.' },
  home_feed: { ratio: '1:1', size: '800 × 800 px', hint: 'Tarjeta cuadrada entre productos.' },
  home_bottom: { ratio: '4:1', size: '1280 × 320 px', hint: 'Banner ancho debajo del listado.' },
  product_sidebar: { ratio: '1:1', size: '600 × 600 px', hint: 'Tarjeta en la ficha de producto.' },
};

export const AdBannerSchema = z.object({
  id: z.string().min(1),
  name: z.string().trim().min(1).max(120),
  type: z.enum(['banner_image', 'html']),
  image_url: z.string().url().max(1000).or(z.literal('')),
  /** Se pinta en un iframe `sandbox` sin scripts (la 1.0 lo saneaba con DOMPurify). */
  html_content: z.string().max(5000),
  click_url: z.string().url().max(1000).startsWith('http').or(z.literal('')),
  /** Vacío = todos los países. */
  target_countries: z.array(CountryCodeSchema).max(40),
  placement: AdPlacementSchema,
  is_active: z.boolean(),
  starts_at: TimestampSchema.nullable(),
  ends_at: TimestampSchema.nullable(),
  impressions: z.number().int().min(0),
  clicks: z.number().int().min(0),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
  created_by: UserIdSchema,
});
export type AdBanner = z.infer<typeof AdBannerSchema>;

/** `settings/adServer` (1.0 platform_config/ad_server; ids públicos, sin secretos). */
export const AdServerSettingsSchema = z.object({
  provider: z.enum(AD_PROVIDERS),
  google_ad_client_id: z.string().trim().max(40),
  google_ad_unit_id: z.string().trim().max(40),
  meta_placement_id: z.string().trim().max(80),
  /** Un anuncio cada N productos del feed (1.0 frecuencia_anuncios = 8). */
  feed_frequency: z.number().int().min(1).max(50),
  updated_at: TimestampSchema,
  updated_by: UserIdSchema,
});
export type AdServerSettings = z.infer<typeof AdServerSettingsSchema>;

export const AD_SERVER_DEFAULTS = {
  provider: 'manual',
  google_ad_client_id: '',
  google_ad_unit_id: '',
  meta_placement_id: '',
  feed_frequency: 8,
} as const satisfies Omit<AdServerSettings, 'updated_at' | 'updated_by'>;

/** Activo, dentro de fechas y dirigido al país (1.0 filterActivePlacementAds). */
export function adIsLive(
  ad: Pick<AdBanner, 'is_active' | 'starts_at' | 'ends_at' | 'target_countries'>,
  now: number,
  country: string | null,
): boolean {
  if (!ad.is_active) return false;
  if (ad.starts_at && ad.starts_at.toMillis() > now) return false;
  if (ad.ends_at && ad.ends_at.toMillis() < now) return false;
  if (ad.target_countries.length > 0 && country) return ad.target_countries.includes(country);
  return true;
}

/** Rotación estable entre campañas del mismo placement (1.0 pickPlacementAd). */
export function pickPlacementAd<T>(ads: readonly T[], slotIndex = 0): T | null {
  if (ads.length === 0) return null;
  return ads[slotIndex % ads.length] ?? null;
}

/** Proveedor efectivo de un slot: híbrido alterna Google (pares) / manual (impares). */
export function resolveSlotProvider(provider: AdProvider, slotIndex: number): AdProvider {
  if (provider === 'hybrid') return slotIndex % 2 === 0 ? 'google' : 'manual';
  return provider;
}

export type FeedItem<T> = { type: 'product'; product: T } | { type: 'ad'; slotIndex: number };

/**
 * Intercala un slot de anuncio cada `frequency` productos (1.0 buildFeedItems). Con pocos
 * productos (p. ej. 2 y frecuencia 8) se añade un slot al final para que el feed tenga uno.
 */
export function buildFeedItems<T>(products: readonly T[], frequency: number): FeedItem<T>[] {
  if (products.length === 0) return [];
  const freq = Number.isFinite(frequency) && frequency >= 1 ? Math.floor(frequency) : 0;
  const items: FeedItem<T>[] = [];
  let slot = 0;
  products.forEach((product, index) => {
    items.push({ type: 'product', product });
    if (freq > 0 && (index + 1) % freq === 0) {
      items.push({ type: 'ad', slotIndex: slot });
      slot += 1;
    }
  });
  if (freq > 0 && slot === 0) items.push({ type: 'ad', slotIndex: 0 });
  return items;
}
